import {environment} from '../environments/environment';
import {NgModule, CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DndModule} from 'ng2-dnd';
import {HttpClient, HttpClientModule} from '@angular/common/http';
import {BrowserModule} from '@angular/platform-browser';
import {CookieModule} from 'ngx-cookie';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {CdkTableModule} from '@angular/cdk/table';
import {TranslateModule, TranslateLoader} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {NgSelectModule} from '@ng-select/ng-select';
import {RouterModule} from '@angular/router';
import {StoreModule} from '@ngrx/store';
import {EffectsModule} from '@ngrx/effects';
import {RouterStateSerializer, StoreRouterConnectingModule} from '@ngrx/router-store';
import {RoutingModule} from './app-routing.module';
import {ConsignmentModule} from '@modules/consignment/consignment.module';
import {AuthenticationModule} from '@modules/authentication/authentication.module';
import {SharedModule} from '@modules/shared/shared.module';
import {DashboardModule} from '@modules/dashboard/dashboard.module';
import {AddressBookModule} from '@modules/address-book/address-book.module';
import {UserSettingsModule} from '@modules/user-settings/user-settings.module';
import {ShipmentsModule} from '@modules/shipments/shipments.module';
import {UsersModule} from '@modules/users/users.module';
import {MaterialModule} from '@modules/material/material.module';
import {DateAdapter, MAT_DATE_LOCALE} from '@angular/material';
import {Api} from './utils';
import {AdminOrdersComponent} from '@components/orders/orders.component';
import {AdminPackagesRulesComponent} from '@components/packages-rules/packages-rules.component';
import {AdminServicesDeliveryComponent} from '@components/services-delivery/services-delivery.component';
import {ConfigsDeliveryComponent} from '@components/services-delivery/configs.component';
import {AdminServicesModesComponent} from '@components/services-modes/services-modes.component';
import {PriceRulesComponent} from '@components/price-rules/price-rules.component';
import {SidenavComponent} from '@components/sidenav/sidenav.component';
import {FooterComponent} from '@components/footer/footer.component';
import {AppComponent} from './app.component';
import {CompaniesComponent, CompanyDialog, RegistrationCompaniesDialog} from '@components/companies/companies.component';
import {UserDashboardComponent} from '@components/user-dashboard/user-dashboard.component';
import {AdminSettingsComponent} from '@components/admin-settings/admin-settings.component';
import {RuleDialog} from '@components/price-rules/rule-dialog/rule-dialog.component';
import {RuleCondition} from '@components/price-rules/rule-condition/rule-condition.component';
import {DialogSharedComponent} from '@components/dialog-shared/dialog-shared.component';
import {PersonalInfoCardComponent} from '@components/personal-info-card/personal-info-card.component';
import {reducers} from '@store/reducers';
import {effects} from '@store/effects';
import {GoogleAnalyticsEventsService} from './services/google-analytics-events.service';
import {StatisticResolve} from '@services/statistics.resolve';
import {AdminService} from '@services/admin.service';
import {AddonService} from '@services/addon.service';
import {PrintDocumentsService} from '@services/print-documents.service';
import {BrowserPrintService} from '@services/browser-print.service';
import {AuthService} from '@services/auth.service';
import {PackageDetailsService} from '@services/package-details.service';
import {PaymentService} from '@services/payment.service';
import {RequestsHandler, MessageDialog} from '@services/requests.service';
import {RoleGuard} from '@services/role.guard';
import {UserService} from '@services/user.service';
import {PricingRulesService} from '@services/pricing-rules.service';
import {OrdersService} from '@services/orders.service';
import {FontAwesomeService} from '@services/font-awesome.service';
import {ShipmentStatusService} from '@services/shipment-status.service';
import {ServicesDeliveryService} from '@services/services-delivery.service';
import {FormValidationService} from '@services/form-validation.service';
import {OrderCreationService} from '@services/order-creation.service';
import {CustomSerializer} from '@store/reducers/router.reducer';
import {SnackBarComponent} from '@components/snackbar/snackbar.component';
import {TrackResolve} from '@services/track.resolve';
import {SearchService} from '@services/search.service';
import {FormUtilService} from '@services/form-util.service';
import {VersionCheckService} from '@services/version-check.service';
import {ngxZendeskWebwidgetModule, ngxZendeskWebwidgetConfig} from 'ngx-zendesk-webwidget';
import {AddressService} from '@services/address.service';
import {CustomDateAdapter} from '@components/form-fields/datepicker-field/custom-date-adapter';
import {CustomsService} from '@services/customs.service';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/locales/', '.json?v=' + environment.version);
  // return new TranslateHttpLoader(http, '/assets/locales/', '.json');
}

export class ZendeskConfig extends ngxZendeskWebwidgetConfig {
  accountUrl = 'freightseeker.zendesk.com';

  beforePageLoad(zE) {
    zE.setLocale('en');
    zE.hide();
  }
}

@NgModule({
  declarations: [
    AppComponent,
    AdminOrdersComponent,
    AdminPackagesRulesComponent,
    AdminServicesDeliveryComponent,
    ConfigsDeliveryComponent,
    AdminServicesModesComponent,
    PriceRulesComponent,
    RuleDialog,
    RuleCondition,
    MessageDialog,
    SidenavComponent,
    CompanyDialog,
    CompaniesComponent,
    RegistrationCompaniesDialog,
    UserDashboardComponent,
    AdminSettingsComponent,
    RuleDialog,
    DialogSharedComponent,
    PersonalInfoCardComponent,
    FooterComponent,
    SnackBarComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RoutingModule,
    RouterModule,
    BrowserAnimationsModule,
    HttpClientModule,
    CdkTableModule,
    DndModule.forRoot(),
    CookieModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    DndModule.forRoot(),
    FontAwesomeModule,
    NgSelectModule,
    // store
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot(effects),
    StoreDevtoolsModule.instrument(),
    StoreRouterConnectingModule,
    // custom modules
    SharedModule,
    MaterialModule,
    ConsignmentModule,
    UsersModule,
    AuthenticationModule,
    DashboardModule,
    ConsignmentModule,
    ShipmentsModule,
    AddressBookModule,
    UserSettingsModule,
    ngxZendeskWebwidgetModule.forRoot(ZendeskConfig)
  ],
  entryComponents: [
    RuleDialog,
    MessageDialog,
    AppComponent,
    CompanyDialog,
    RegistrationCompaniesDialog,
    DialogSharedComponent,
    SnackBarComponent
  ],
  providers: [
    Api,
    // AdminRouteGuard,
    RequestsHandler,
    RoleGuard,
    UserService,
    AddressService,
    GoogleAnalyticsEventsService,
    PricingRulesService,
    AuthService,
    ServicesDeliveryService,
    FormValidationService,
    FormUtilService,
    OrdersService,
    FontAwesomeService,
    ShipmentStatusService,
    AddonService,
    PrintDocumentsService,
    BrowserPrintService,
    PackageDetailsService,
    PaymentService,
    // resolvers
    AdminService,
    StatisticResolve,
    TrackResolve,
    OrderCreationService,
    SearchService,
    VersionCheckService,
    CustomsService,
    {provide: RouterStateSerializer, useClass: CustomSerializer},
    {provide: MAT_DATE_LOCALE, useValue: 'sv-SE'},
    {provide: DateAdapter, useClass: CustomDateAdapter},
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})

export class AppModule {
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts